<template>
  <div class="position-relative">

    <!-- Search -->
    <b-card class="mt-4 shadow">
      <div style="max-width: 450px;">
        <label class="font-weight-bold">
          Search by Email / Mobile / Name / Admission No:
        </label>
        <div class="input-group">
          <input
            v-model="searchKey"
            class="form-control"
            placeholder="Enter Email / Mobile / Name / Admission No"
            @keyup.enter="searchStudents"
          />
          <span class="input-group-text bg-light" @click="searchStudents">
            <i class="fas fa-search text-primary"></i>
          </span>
        </div>
      </div>
    </b-card>

    <div v-if="noStudentFound" class="alert alert-danger mt-3 text-center">
      No student found with fee records.
    </div>

    <!-- Student List -->
    <div v-if="!student && students.length" class="mt-3">
      <table class="table table-sm table-bordered">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Admission No</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in students" :key="s.id">
            <td>{{ s.name }}</td>
            <td>{{ s.email }}</td>
            <td>{{ s.mobile }}</td>
            <td>{{ s.admission_no }}</td>
            <td><b-button size="sm" variant="primary" @click="loadStudent(s.id)">View</b-button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="student" class="mt-3">
      <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
        <h5 class="font-weight-bold mb-0">
          {{ student.first_name }} {{ student.last_name }}
          <small v-if="feeStructure" class="text-muted font-weight-normal">({{ feeStructure }})</small>
        </h5>
        <span class="font-weight-bold">Fee Credit: {{ formatAmount(walletBalance) }}</span>
      </div>

      <!-- Refund from bills -->
      <h6 class="font-weight-bold">Refund from Bills</h6>
      <div v-if="!rows.length" class="alert alert-secondary">No paid bills to refund.</div>

      <template v-else>
        <div class="table-responsive" style="height: auto !important;">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>Component</th>
                <th>Bill</th>
                <th class="text-right">Bill Amount</th>
                <th class="text-right">Paid</th>
                <th style="width: 180px;">Refund Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.group_id">
                <td>{{ row.component }}</td>
                <td>
                  {{ row.bill_name || '—' }}
                  <small class="text-muted d-block">{{ row.bill_number }}</small>
                </td>
                <td class="text-right">{{ formatAmount(row.amount) }}</td>
                <td class="text-right">
                  {{ formatAmount(row.credit) }}
                  <small v-if="row.online > 0" class="text-muted d-block">Online: {{ formatAmount(row.online) }}</small>
                </td>
                <td>
                  <input
                    v-model.number="refundAmounts[row.group_id]"
                    type="number"
                    class="form-control form-control-sm"
                    min="0"
                    :max="row.credit"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted d-block mb-3">
          Refunding everything paid on a bill closes that bill. Leave non-refundable fees at 0.
        </small>

        <b-form-group label="Refund to:" class="font-weight-bold">
          <b-form-radio-group v-model="destination" :options="destinations" />
        </b-form-group>

        <fee-refund-details
          :show-reference="destination === 'source' && refundOffline > 0"
          :details="refundDetails"
          :payout-modes="payoutModes"
        />

        <small v-if="destination === 'source' && refundTotal > 0" class="text-muted d-block mb-2">
          <template v-if="refundOnline > 0">
            {{ formatAmount(refundOnline) }} goes back to the student's online payment.
          </template>
          <template v-if="refundOffline > 0">
            {{ formatAmount(refundOffline) }} is recorded as paid back the way it was paid - hand it over to the student.
          </template>
        </small>

        <div class="d-flex justify-content-between align-items-center mb-4">
          <strong>Total Refund: {{ formatAmount(refundTotal) }}</strong>
          <b-button variant="danger" :disabled="!canRefund || saving" @click="submitRefund">
            Refund
          </b-button>
        </div>
      </template>

      <!-- Pay out Fee Credit -->
      <template v-if="walletBalance > 0">
        <h6 class="font-weight-bold">Pay Out Fee Credit</h6>
        <b-form-group label="Amount:">
          <input
            v-model.number="payoutAmount"
            type="number"
            class="form-control"
            style="max-width: 200px;"
            min="0"
            :max="walletBalance"
          />
        </b-form-group>

        <small v-if="walletOnline > 0" class="text-muted d-block mb-2">
          Up to {{ formatAmount(walletOnline) }} goes back to the student's online payment.
          <template v-if="payoutOffline > 0">
            Choose how the remaining {{ formatAmount(payoutOffline) }} is paid out.
          </template>
        </small>

        <fee-refund-details
          :show-mode="payoutOffline > 0"
          :show-reference="payoutOffline > 0"
          :details="payoutDetails"
          :payout-modes="payoutModes"
        />

        <div class="text-right mb-4">
          <b-button variant="danger" :disabled="!canPayOut || saving" @click="submitPayout">
            Record Payout
          </b-button>
        </div>
      </template>

      <!-- History -->
      <template v-if="refunds.length">
        <h6 class="font-weight-bold">Refund History</h6>
        <div class="table-responsive" style="height: auto !important;">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>Date</th>
                <th class="text-right">Amount</th>
                <th>Type</th>
                <th>Reference</th>
                <th>Reason</th>
                <th>Approved By</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="refund in refunds" :key="refund.id">
                <td>{{ formatDate(refund.created_at) }}</td>
                <td class="text-right">{{ formatAmount(refund.amount) }}</td>
                <td>{{ refundType(refund) }}</td>
                <td>{{ refund.reference_no || '—' }}</td>
                <td>{{ refund.reason }}</td>
                <td>{{ refund.approved_by }}</td>
                <td>
                  {{ refund.processed_by
                    ? `${refund.processed_by.first_name} ${refund.processed_by.last_name}`
                    : '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import FeeRefundDetails from "./feeRefundDetails.vue";

const emptyDetails = () => ({
  payout_mode: null,
  reference_no: "",
  reason: "",
});

export default {
  components: { FeeRefundDetails },

  data() {
    return {
      searchKey: "",
      students: [],
      noStudentFound: false,

      student: null,
      feeStructure: "",
      rows: [],
      walletBalance: 0,
      walletOnline: 0,
      refunds: [],

      refundAmounts: {},
      destination: "wallet",
      refundDetails: emptyDetails(),

      payoutAmount: 0,
      payoutDetails: emptyDetails(),

      saving: false,

      destinations: [
        { value: "wallet", text: "Fee Credit (student's wallet)" },
        { value: "source", text: "Back to the student" },
      ],
      gatewayStatusLabels: {
        initiating: "Processing",
        processing: "Processing",
        refunded: "Refunded",
        failed: "Failed",
      },
      payoutModes: [
        { value: null, text: "Select" },
        "Cash",
        "Bank Transfer",
        "Cheque",
        "UPI",
      ],
    };
  },

  computed: {
    apiUrl() {
      return this.$store.getters.getAPIKey.mainAPI;
    },

    refundLines() {
      return this.rows
        .filter((row) => Number(this.refundAmounts[row.group_id]) > 0)
        .map((row) => ({ group_id: row.group_id, amount: Number(this.refundAmounts[row.group_id]) }));
    },

    refundTotal() {
      return this.refundLines.reduce((sum, line) => sum + line.amount, 0);
    },

    refundsWithinPaid() {
      return this.refundLines.every((line) => {
        const row = this.rows.find((r) => r.group_id === line.group_id);
        return line.amount <= row.credit;
      });
    },

    // Online money goes back first - the same split the server makes.
    refundOnline() {
      return this.refundLines.reduce((sum, line) => {
        const row = this.rows.find((r) => r.group_id === line.group_id);
        return sum + Math.min(line.amount, row.online || 0);
      }, 0);
    },

    refundOffline() {
      return Math.max(0, Math.round((this.refundTotal - this.refundOnline) * 100) / 100);
    },

    payoutOffline() {
      return Math.max(0, Math.round((this.payoutAmount - this.walletOnline) * 100) / 100);
    },

    canRefund() {
      return this.refundLines.length > 0
        && this.refundsWithinPaid
        && this.detailsComplete(this.refundDetails);
    },

    canPayOut() {
      return this.payoutAmount > 0
        && this.payoutAmount <= this.walletBalance
        && this.detailsComplete(this.payoutDetails)
        && (this.payoutOffline === 0 || !!this.payoutDetails.payout_mode);
    },
  },

  methods: {
    detailsComplete(details) {
      return !!details.reason.trim();
    },

    async searchStudents() {
      this.noStudentFound = false;
      this.student = null;

      const res = await this.$axios.get(`${this.apiUrl}/studentcategorymappings/view-ledger/student-list`, {
        params: { search: this.searchKey },
      });

      this.students = res.data || [];
      this.noStudentFound = !this.students.length;
    },

    async loadStudent(institutionUserId) {
      const res = await this.$axios.get(`${this.apiUrl}/feerefunds/student`, {
        params: { institution_user_id: institutionUserId },
      });

      this.student = res.data.student;
      this.feeStructure = res.data.fee_structure;
      this.rows = res.data.rows;
      this.walletBalance = res.data.wallet_balance;
      this.walletOnline = res.data.wallet_online;
      this.refunds = res.data.refunds;

      this.refundAmounts = {};
      this.refundDetails = emptyDetails();
      this.payoutAmount = 0;
      this.payoutDetails = emptyDetails();
    },

    async submitRefund() {
      const where = this.destination === "wallet" ? "Fee Credit" : "the student";
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Refund ${this.formatAmount(this.refundTotal)} to ${where}? This can't be undone.`,
        { centered: true, okVariant: "danger", okTitle: "Refund" }
      );
      if (!confirmed) return;

      await this.save(`${this.apiUrl}/feerefunds`, {
        ...this.refundDetails,
        institution_user_id: this.student.id,
        destination: this.destination,
        lines: this.refundLines,
      });
    },

    async submitPayout() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Record a payout of ${this.formatAmount(this.payoutAmount)}? This can't be undone.`,
        { centered: true, okVariant: "danger", okTitle: "Record Payout" }
      );
      if (!confirmed) return;

      await this.save(`${this.apiUrl}/feerefunds/wallet-payout`, {
        ...this.payoutDetails,
        institution_user_id: this.student.id,
        amount: this.payoutAmount,
      });
    },

    async save(url, payload) {
      this.saving = true;
      try {
        const res = await this.$axios.post(url, payload);
        const { refunds, warnings } = res.data;

        // Part of a refund can be refused by the gateway while the rest stands.
        (warnings || []).forEach((warning) => this.$toast.error(warning, { duration: 10000 }));
        if (refunds && refunds.length) {
          this.$toast.success("Refund recorded");
        }

        await this.loadStudent(this.student.id);
      } finally {
        this.saving = false;
      }
    },

    refundType(refund) {
      if (refund.destination === "wallet") return "To Fee Credit";
      const kind = refund.from_wallet ? "Fee Credit payout" : "Refund";
      if (refund.gateway_status) {
        return `${kind} to online payment (${this.gatewayStatusLabels[refund.gateway_status]})`;
      }
      return `${kind} (${refund.payout_mode})`;
    },

    formatDate(value) {
      return new Date(value).toLocaleDateString("en-GB");
    },

    formatAmount(v) {
      return Number(v || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
      });
    },
  },
};
</script>
